import React, { useEffect, useMemo, useState } from "react";
import { Spinner } from "react-bootstrap";
import { SubTitle } from "../../Components/CustomComponents";
import Toast from "../../Hooks/useToast";
import { getService } from "../../redux/services";
import { api_urls } from "../../utilities";
import styles from "./styles/analytics.module.scss";

const toNumber = (value) => {
  const num = Number(value);
  return Number.isNaN(num) ? 0 : num;
};

const getEnvelopeData = (response) => {
  const envelope = response?.data ?? {};
  return envelope?.data ?? envelope;
};

const normalizeTrend = (list = []) => {
  if (!Array.isArray(list)) return [];
  return list.map((item, index) => ({
    id: `${item?.label || item?.month || "item"}-${index}`,
    label: item?.label || `${item?.month || "-"} ${item?.year || ""}`.trim(),
    value:
      toNumber(item?.total) ||
      toNumber(item?.users) ||
      toNumber(item?.earnings) ||
      toNumber(item?.completed),
  }));
};

const buildBars = (source = []) => {
  const maxValue = Math.max(...source.map((entry) => entry.value), 1);
  return source.map((entry) => ({
    ...entry,
    percentage: Math.max((entry.value / maxValue) * 100, 4),
  }));
};

const formatCurrency = (value) =>
  `$${toNumber(value).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

export default function AnalyticsScreen() {
  const [loading, setLoading] = useState(true);
  const [earningsData, setEarningsData] = useState({});
  const [usersData, setUsersData] = useState({});
  const [appointmentsData, setAppointmentsData] = useState({});
  const [period, setPeriod] = useState("today");

  useEffect(() => {
    const fetchAnalytics = async () => {
      setLoading(true);
      try {
        const params = { period };
        const [earningsRes, usersRes, appointmentsRes] = await Promise.all([
          getService(api_urls.analyticsEarnings, params),
          getService(api_urls.analyticsUsers, params),
          getService(api_urls.analyticsAppointments, params),
        ]);

        setEarningsData(getEnvelopeData(earningsRes));
        setUsersData(getEnvelopeData(usersRes));
        setAppointmentsData(getEnvelopeData(appointmentsRes));
      } catch (error) {
        const msg = error?.data?.message ?? error?.message ?? "Failed to fetch analytics";
        Toast(msg, "error");
      } finally {
        setLoading(false);
      }
    };

    fetchAnalytics();
  }, [period]);

  const overviewCards = useMemo(() => {
    const earningsSummary = earningsData?.summary ?? {};
    const usersSummary = usersData?.summary ?? {};
    const appointmentsSummary = appointmentsData?.summary ?? {};

    return [
      {
        label: "Total Earnings",
        value: loading ? "..." : formatCurrency(earningsSummary?.total_earnings),
      },
      {
        label: "Total Users",
        value: loading ? "..." : toNumber(usersSummary?.total_users).toLocaleString("en-US"),
      },
      {
        label: "New Users",
        value: loading ? "..." : toNumber(usersSummary?.new_users).toLocaleString("en-US"),
      },
      {
        label: "Total Appointments",
        value: loading ? "..." : toNumber(appointmentsSummary?.total).toLocaleString("en-US"),
      },
      {
        label: "Completed Appointments",
        value: loading ? "..." : toNumber(appointmentsSummary?.completed).toLocaleString("en-US"),
      },
      {
        label: "Pending Appointments",
        value: loading ? "..." : toNumber(appointmentsSummary?.pending).toLocaleString("en-US"),
      },
    ];
  }, [appointmentsData, earningsData, loading, usersData]);

  const earningsTrendBars = useMemo(
    () => buildBars(normalizeTrend(earningsData?.monthly_trend)),
    [earningsData]
  );
  const usersTrendBars = useMemo(
    () => buildBars(normalizeTrend(usersData?.monthly_trend)),
    [usersData]
  );
  const appointmentsTrendBars = useMemo(
    () => buildBars(normalizeTrend(appointmentsData?.monthly_trend)),
    [appointmentsData]
  );

  const earningsByLocation = Array.isArray(earningsData?.by_location)
    ? earningsData.by_location
    : [];
  const appointmentsByLocation = Array.isArray(appointmentsData?.by_location)
    ? appointmentsData.by_location
    : [];

  return (
    <div className={styles.analytics}>
      <div className={styles.headerRow}>
        <SubTitle text="Analytics" />
        <div className={styles.filterWrap}>
          <label htmlFor="appointments-period" className={styles.filterLabel}>
            Period
          </label>
          <select
            id="appointments-period"
            className={styles.filterSelect}
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
          >
            <option value="today">Today</option>
            <option value="week">Week</option>
            <option value="month">Month</option>
            <option value="year">Year</option>
          </select>
        </div>
      </div>

      {loading && (
        <div className={styles.loaderBox}>
          <Spinner animation="border" variant="light" />
          <p>Loading analytics data...</p>
        </div>
      )}

      <section className={styles.cardsGrid}>
        {overviewCards.map((card) => (
          <div className={styles.card} key={card.label}>
            <p className={styles.cardLabel}>{card.label}</p>
            <h3 className={styles.cardValue}>{card.value}</h3>
          </div>
        ))}
      </section>

      <section className={styles.panel}>
        <h3>Earnings Trend</h3>
        {earningsTrendBars.length ? (
          <div className={styles.chartList}>
            {earningsTrendBars.map((bar) => (
              <div className={styles.chartRow} key={bar.id}>
                <span className={styles.chartLabel}>{bar.label}</span>
                <div className={styles.barTrack}>
                  <div className={styles.barFill} style={{ width: `${bar.percentage}%` }} />
                </div>
                <span className={styles.chartValue}>{formatCurrency(bar.value)}</span>
              </div>
            ))}
          </div>
        ) : (
          <p className={styles.empty}>No earnings trend data</p>
        )}
      </section>

      <section className={styles.panel}>
        <h3>Users Trend</h3>
        {usersTrendBars.length ? (
          <div className={styles.chartList}>
            {usersTrendBars.map((bar) => (
              <div className={styles.chartRow} key={bar.id}>
                <span className={styles.chartLabel}>{bar.label}</span>
                <div className={styles.barTrack}>
                  <div className={styles.barFill} style={{ width: `${bar.percentage}%` }} />
                </div>
                <span className={styles.chartValue}>{bar.value}</span>
              </div>
            ))}
          </div>
        ) : (
          <p className={styles.empty}>No users trend data</p>
        )}
      </section>

      <section className={styles.panel}>
        <h3>Appointments Trend</h3>
        {appointmentsTrendBars.length ? (
          <div className={styles.chartList}>
            {appointmentsTrendBars.map((bar) => (
              <div className={styles.chartRow} key={bar.id}>
                <span className={styles.chartLabel}>{bar.label}</span>
                <div className={styles.barTrack}>
                  <div className={styles.barFill} style={{ width: `${bar.percentage}%` }} />
                </div>
                <span className={styles.chartValue}>{bar.value}</span>
              </div>
            ))}
          </div>
        ) : (
          <p className={styles.empty}>No appointments trend data</p>
        )}
      </section>

      <section className={styles.twoColGrid}>
        <div className={styles.panel}>
          <h3>Earnings By Location</h3>
          {earningsByLocation.length ? (
            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Location</th>
                    <th>Earnings</th>
                    <th>Invoices</th>
                  </tr>
                </thead>
                <tbody>
                  {earningsByLocation.map((item) => (
                    <tr key={item.location_id}>
                      <td>{item.location_name || "-"}</td>
                      <td>{formatCurrency(item.earnings)}</td>
                      <td>{toNumber(item.invoice_count)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className={styles.empty}>No earnings location data</p>
          )}
        </div>

        <div className={styles.panel}>
          <h3>Appointments By Location</h3>
          {appointmentsByLocation.length ? (
            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Location</th>
                    <th>Total</th>
                    <th>Completed</th>
                    <th>Cancelled</th>
                  </tr>
                </thead>
                <tbody>
                  {appointmentsByLocation.map((item) => (
                    <tr key={item.location_id}>
                      <td>{item.location_name || "-"}</td>
                      <td>{toNumber(item.total)}</td>
                      <td>{toNumber(item.completed)}</td>
                      <td>{toNumber(item.cancelled)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className={styles.empty}>No appointment location data</p>
          )}
        </div>
      </section>
    </div>
  );
}
